import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as BookingService from '_services/booking-service';
import { sub, add, format } from 'date-fns';
import { PRMCodes } from '_utils/constants';

function formatDate(dateString) {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = `0${date.getUTCMonth() + 1}`.slice(-2);
  const day = `0${date.getUTCDate()}`.slice(-2);
  const hours = `0${date.getUTCHours()}`.slice(-2);
  const minutes = `0${date.getUTCMinutes()}`.slice(-2);
  return `${year}-${month}-${day} (${hours}:${minutes})`;
}

const initialState = {
  departures: [],
  filters: [],
  count: 0,
  skip: 0,
  countStatus: 'idle',
  status: 'idle',
  statusObj: {},
  error: null
};

// get all bookings  
export const getDepartures = createAsyncThunk('departures/getDepartures', async (params, signal) => {
  return BookingService.getDepartures(params, signal);
});

export const getDeparturesWithSearch = createAsyncThunk('departures/getDeparturesWithSearch', async (params, signal) => {
  return BookingService.getDeparturesWithSearch(params, signal);
});




// get count for list
export const getDeparturesCount = createAsyncThunk('departures/getDeparturesCount', async (params, signal) => {
  return BookingService.getArrivalsOrDeparturesCount(params, signal);
});

const departuresSlice = createSlice({
  name: 'departures',
  initialState,
  reducers: {
    reset: (state) => initialState,
    resetSkip: (state) => {
      return {
        ...state,
        skip: 0
      }
    },
    IncreaseDepartureSkip: (state, action) => {
      return {
        ...state,
        skip: state.skip + action.payload
      }
    },
    DecreaseDepartureSkip: (state, action) => {
      return {
        ...state,
        skip: state.skip - action.payload
      }
    },
    setFilters: (state, action) => {
      return {
        ...state,
        filters: action.payload,
      }
    }
  },
  extraReducers: {
    [getDepartures.pending]: (state, action) => {
      return {
        ...state,
        status: 'loading',
      }
    },
    [getDepartures.fulfilled]: (state, action) => {
      return {
        ...state,
        status: 'succeeded',
        departures: action.payload,
      }
    },
    [getDepartures.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.status,
        error: !action.meta.aborted ? action.error.message : state.error
      }
    },

    [getDeparturesWithSearch.pending]: (state, action) => {
      return {
        ...state,
        status: 'loading',
      }
    },
    [getDeparturesWithSearch.fulfilled]: (state, action) => {
      return {
        ...state,
        status: 'succeeded',
        departures: action.payload,
        count: action.payload.length,
      }
    },
    [getDeparturesWithSearch.rejected]: (state, action) => {
      return {
        ...state,
        status: !action.meta.aborted ? "failed" : state.status,
        error: !action.meta.aborted ? action.error.message : state.error
      }
    },

    [getDeparturesCount.pending]: (state, action) => {
      return {
        ...state,
        countStatus: 'loading',
      }
    },
    [getDeparturesCount.fulfilled]: (state, action) => {
      return {
        ...state,
        countStatus: 'succeeded',
        count: action.payload.totalDepartures,
      }
    },
    [getDeparturesCount.rejected]: (state, action) => {
      return {
        ...state,
        countStatus: !action.meta.aborted ? "failed" : state.countStatus,
        error: !action.meta.aborted ? action.error.message : state.error,
      }
    },
  }
});

export const departureList = (filter) => (state) => {
  // if null or undefined return empty
  if (state.departures.departures == []) { return [] }
  if (state.departures.departures == undefined) { return [] }

  // bookings exsist, create new list object for table
  let list = [];


  function processAssistance(assistance) {
    if (!assistance) { return [''] }
    let result = []
    assistance.forEach((a) => {
      for (const r in PRMCodes) {
        let regex = PRMCodes[r]
        if (a && a.includes(regex)) {
          result.push(regex)
        }
      }
    });
    result = [...new Set(result)]
    return result
  }


  // used to capitalize and remove initals from string
  function processName(first, second) {
    if (!first && !second) { return '' }
    // MR MISS MRS MS
    if (first.slice(first.length - 3) == 'MRS') {
      return ('Mrs ' + capitalize(first.substring(0, first.length - 3)) + ' ' + capitalize(second))
    }
    else if (first.slice(first.length - 2) == 'MS') {
      return ('Ms ' + capitalize(first.substring(0, first.length - 2)) + ' ' + capitalize(second))
    }
    else if (first.slice(first.length - 2) == 'MR') {
      return ('Mr ' + capitalize(first.substring(0, first.length - 2)) + ' ' + capitalize(second))
    }
    else if (first.slice(first.length - 4) == 'MISS') {
      return ('Miss ' + capitalize(first.substring(0, first.length - 4)) + ' ' + capitalize(second))
    }
    else {
      return (capitalize(first) + ' ' + capitalize(second))
    }
  }


  //console.log('departures', state.departures.departures);
  // capitalize names
  function capitalize(string) {
    if (string && string[0]) {
      return string[0].toUpperCase() + string.slice(1).toLowerCase();
    } else return string;
  }

  let departuresCount = 0;


  state.departures.departures.forEach(booking => {
    departuresCount++
    let fullName = ''

    if (booking && booking.name && booking.assistance != undefined) {
      fullName = booking.name.split(' ');

      list.push({
        count: departuresCount + state.departures.skip,
        name: processName(fullName[0], fullName[1]),
        assistances: processAssistance(booking.assistance),
        date: booking.flightConfirmationDetails.departureDate ? booking.flightConfirmationDetails.departureDate : "",
        flightNumber: booking.flightConfirmationDetails.carrierCode ? booking.flightConfirmationDetails.carrierCode + booking.flightConfirmationDetails.flightNumber : "",
        airport: booking.flightConfirmationDetails.destination ? booking.flightConfirmationDetails.destination : "",
        type: booking.userId == 'SITA' ? 'SITA' : 'PRM Assist',
        flightTime: ' ',
        contact: ' ',
        dueTime: booking.dueTime ? booking.dueTime : 'N/A',
        bio: booking.userId == 'SITA' ? booking.assistance.join("") : booking.bio ? booking.bio : "",
        arrivalTime: !isNaN(new Date(booking.flightConfirmationDetails.arrivalTime).getTime()) && booking.flightConfirmationDetails.arrivalTime ? format(new Date(booking.flightConfirmationDetails.arrivalTime), "h:mm a") : "N/A",
        departureTime: !isNaN(new Date(booking.flightConfirmationDetails.departureTime).getTime()) && booking.flightConfirmationDetails.departureTime ? format(new Date(booking.flightConfirmationDetails.departureTime), "h:mm a") : "N/A",
        dateCreated: formatDate(booking.dateCreated),
        //timePreadvised: (new Date(booking.dateCreated) - new Date(booking.flightConfirmationDetails.departureTime)) / (60 * 60 * 1000),
        leg1Assistance: booking.originAssistance ? 'Requested' : 'Not Requested',
        leg2Assistance: booking.destinationAssistance ? 'Requested' : 'Not Requested'
      })
    }
  });

  return list;
};

export const { reset, resetSkip, IncreaseDepartureSkip, DecreaseDepartureSkip, setFilters } = departuresSlice.actions;
export const selectDepartures = state => state.departures.departures;
export const selectDepartureCount = state => state.departures.count;
export const selectDepartureCountStatus = state => state.departures.countStatus;
export const selectDepartureStatus = state => state.departures.status;
export const selectDepartureSkip = state => state.departures.skip;
export const selectFilters = state => state.departures.filters;

export default departuresSlice.reducer;
